import { StandardEvents } from '@shopify/events';

/**
 * Re-renders a server-rendered fragment when the shopper picks another variant.
 *
 * Horizon's own blocks (price, buy buttons) each listen for the product select
 * event and swap themselves out of the section HTML the variant picker already
 * fetched. This does the same for any Le Parfait block that depends on the
 * selected variant, so those blocks stay plain Liquid: the per-jar price, the
 * size and fit cue and the Uses & recipes tab render on the server, are
 * readable without JavaScript, and update in place.
 *
 * Blocks in the product information section come straight out of the HTML the
 * picker fetched. Blocks in any other section (the product tabs) are not in
 * that HTML, so the section fetches its own copy for the new variant; one
 * request per section, shared by every synced block in it.
 *
 * Usage: <lp-variant-sync data-sync-id="{{ block.id }}">…</lp-variant-sync>
 */

/** @type {Map<string, Promise<Document | null>>} */
const sectionFetches = new Map();

function fetchSection(sectionId, variantId) {
  const key = `${sectionId}:${variantId}`;
  if (!sectionFetches.has(key)) {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    url.searchParams.set('section_id', sectionId);
    url.hash = '';
    sectionFetches.set(
      key,
      fetch(url)
        .then((response) => (response.ok ? response.text() : null))
        .then((text) => (text ? new DOMParser().parseFromString(text, 'text/html') : null))
        .catch(() => null)
    );
  }
  return sectionFetches.get(key);
}

class LpVariantSync extends HTMLElement {
  connectedCallback() {
    document.addEventListener(StandardEvents.productSelect, this.#onSelect);
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.productSelect, this.#onSelect);
  }

  #selector() {
    return `lp-variant-sync[data-sync-id="${this.dataset.syncId}"]`;
  }

  #onSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card, dialog')) return;

    event.promise
      ?.then(async (result) => {
        let next = result?.detail?.html?.querySelector(this.#selector());

        if (!next) {
          const section = this.closest('.shopify-section');
          const variantId = result?.variant?.id ?? new URLSearchParams(window.location.search).get('variant');
          if (!section || !variantId) return;
          const html = await fetchSection(section.id.replace(/^shopify-section-/, ''), variantId);
          next = html?.querySelector(this.#selector());
        }

        if (next) this.innerHTML = next.innerHTML;
      })
      .catch(() => {});
  };
}

if (!customElements.get('lp-variant-sync')) customElements.define('lp-variant-sync', LpVariantSync);
